<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in navigationRoutes.routes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
            @click="handleItemClick(route)"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>

    <!-- Sidebar Footer -->
    <div class="sidebar-footer">
      <div class="footer-content">
        <div class="app-info">
          <div class="app-version">
            <VaIcon name="info" size="14px" color="secondary" />
            <span class="version-text">v1.0.0</span>
          </div>
          <div class="copyright">
            <span class="copyright-text">© 2025 İhlal Bildirim Sistemi</span>
          </div>
        </div>

        <div class="footer-divider"></div>

        <div class="logout-section">
          <VaButton color="danger" preset="secondary" class="logout-button" @click="handleLogout">
            <VaIcon name="logout" size="18px" class="mr-2" />
            Oturumu Kapat
          </VaButton>
        </div>
      </div>
    </div>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`)
      }

      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    const setActiveExpand = () =>
      (value.value = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => {
      return routeHasActiveChild(route) ? 'primary' : 'secondary'
    }
    const textColor = (route: INavigationRoute) => {
      return routeHasActiveChild(route) ? 'primary' : 'textPrimary'
    }
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleItemClick = (_route: INavigationRoute) => {
      // No special handling needed for regular routes
    }

    const handleLogout = () => {
      localStorage.removeItem('trafix_admin_token')
      localStorage.removeItem('trafix_admin_user')
      router.push('/auth/login')
    }

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      handleItemClick,
      handleLogout,
    }
  },
})
</script>

<style scoped>
.sidebar-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--va-background-secondary);
  border-top: 1px solid var(--va-background-border);
  padding: 1rem;
}

.footer-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.logout-section {
  display: flex;
  justify-content: center;
  width: 100%;
}

.logout-button {
  width: 100%;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.75rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.logout-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
}

.footer-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--va-background-border), transparent);
  margin: 0.25rem 0;
}

.app-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.app-version {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.version-text {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--va-text-primary);
  opacity: 0.7;
}

.copyright-text {
  font-size: 0.65rem;
  color: var(--va-text-primary);
  opacity: 0.5;
  text-align: center;
  line-height: 1.2;
}

/* Adjust sidebar to accommodate footer */
:deep(.va-sidebar) {
  position: relative;
  padding-bottom: 100px;
}

:deep(.va-accordion) {
  height: calc(100vh - 100px);
  overflow-y: auto;
}
</style>
